import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PAGE_NAMES } from '../../../core/helpers/constants';
import { IPermission } from '../../../core/models/auth.models';
import { IClient } from '../../../core/models/clients.models';
import { LoadDataFromServer } from '../../../shared/components/datatable/datatable.model';
import { AppService } from '../../../store/app.service';
import { ClientsApiService } from '../clients.apiservice';
import { ClientsService } from '../clients.service';
import { MergeClientsModal } from '../../../shared/modals/merge-clients/merge-clients.component';
import { InstanceService } from '../../../core/instance/instance.service';
import { ISearch } from '../../../core/models/search.model';

@Component({
  selector: 'app-clients',
  templateUrl: './clients-main.component.html',
  styleUrls: ['./clients-main.component.scss']
})
export class ClientsMainComponent implements OnInit {
  doWhat: IPermission = {} as IPermission;
  columns = [
    { title: 'FIRST_NAME', field: 'firstName', sortable: true, searchable: true },
    { title: 'LAST_NAME', field: 'lastName', sortable: true, searchable: true },
    { title: 'COMPANY', field: 'brand', sortable: true, searchable: true },
    { title: 'DMS', field: 'dmsName', sortable: true, searchable: true },
    { title: 'SALES_REP', field: 'salesRep', sortable: true, searchable: true },
    { title: 'PHONE', field: 'phone', sortable: true, searchable: true },
    { title: 'EMAIL', field: 'email', sortable: true, searchable: true },
  ];
  clientsData: Array<IClient>
  savedSearchId: any;
  clientSavedSearches: ISearch[] = [];
  selectedSavedSearchId: string | null = null;
  private loadEvent!: LoadDataFromServer;
  dataTotalCount: number = 0;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
    private cService: ClientsService,
    private clientService: ClientsApiService,
    private appService: AppService,
    private instance: InstanceService) {
    this.clientsData = new Array<IClient>();
  }

  ngOnInit(): void {
    if (this.instance.isAltLayout) {
      this.columns = [
        { title: 'CLIENT_NAME', field: 'lastName', sortable: true, searchable: true },
        { title: 'COMPANY', field: 'brand', sortable: true, searchable: true },
        { title: 'DMS', field: 'dmsName', sortable: true, searchable: true },
      ];
    }

    this.appService.setPageTitle$('CLIENTS');
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Clients);
    this.appService.getAllClients$()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: any) => {
        this.clientsData = [...response.data];
        this.dataTotalCount = response.totalCount;
      });
    this.savedSearchId = this.route.snapshot.queryParams['savedsearchid'];
    this.loadSavedSearches();
  }

  private loadSavedSearches() {
    this.appService.fetchSearches();
    this.appService.getAllSearches$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((searches: ISearch[]) => {
        this.clientSavedSearches = (searches || [])
          .filter(search => search.resultType?.toLowerCase() === 'client');
        this.selectedSavedSearchId = this.clientSavedSearches.some(search => search.id === this.savedSearchId)
          ? this.savedSearchId
          : null;
      });
  }

  onLoadingData(event: LoadDataFromServer) {
    this.loadEvent = event;
    if (event.search && this.selectedSavedSearchId) {
      this.savedSearchId = '';
      this.selectedSavedSearchId = null;
    }
    this.appService.fetchAllClients({
      SavedSearchId: this.savedSearchId,
      ...this.loadEvent
    });
  }

  onSavedSearchChange(savedSearchId: string | null) {
    this.selectedSavedSearchId = savedSearchId || null;
    this.savedSearchId = savedSearchId || '';
    this.appService.fetchAllClients({
      SavedSearchId: this.savedSearchId,
      ...this.loadEvent
    });
  }

  editRow(evt: any) {
    this.cService.openEditClientModal(evt, {
      SavedSearchId: this.savedSearchId,
      ...this.loadEvent
    });
  }

  onAddNew() {
    this.cService.openEditClientModal({ id: null }, {});
  }

  deleteRows(evt: any) {
    this.clientService.deleteClient(evt)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.appService.fetchAllClients({
          SavedSearchId: this.savedSearchId,
          ...this.loadEvent
        });
      })
  }

  getRowLink = (row: any) => {
    if (this.instance.isAltLayout) {
      return `/pages/location-details/${row.id}`;
    }
    return `/pages/client-details/${row.id}`;
  }


  ngAfterViewInit() {

  }

  handleMergeRecordsClicked(evt: string[]) {
    let clientsToMerge = this.clientsData.filter(f => evt.includes(f.id));
    let size = clientsToMerge.length == 2 ? 'lg' : 'xl';

    const dialogRef = this.modalService.open(MergeClientsModal, { size: size });

    dialogRef.componentInstance.clientsToMerge = clientsToMerge;
    const dialogSubmitSubscription = dialogRef.componentInstance.oMergeComplete.subscribe((r: any) => {
      this.appService.fetchAllClients({
        SavedSearchId: this.savedSearchId,
        ...this.loadEvent
      });
      dialogSubmitSubscription.unsubscribe();
    })
  }

  async export() {
    this.clientService.exportClients({
      SavedSearchId: this.savedSearchId,
      ...this.loadEvent
    }).then((x: any) => {
      var newBlob = new Blob([x], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const data = window.URL.createObjectURL(newBlob);
      var link = document.createElement('a');
      link.href = data;
      link.download = "clients.xlsx";
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
        link.remove();
      }, 100);
    });
  }
}
